import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;
const HOMEPAGE_CARD_VARIANT = "SelectedArt2";
const INITIAL_CARD_COUNT = 6;
const CARD_BATCH_SIZE = 6;

const SelectedWorkArt = () => {
  const [galleries, setGalleries] = useState([]);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_CARD_COUNT);
  const loadMoreRef = useRef(null);

  const visibleGalleries = galleries.slice(0, visibleCount);
  const totalPages = Math.ceil(visibleGalleries.length / slidesPerView);
  const currentPage = Math.floor(currentIndex / slidesPerView);

  useEffect(() => {
    const fetchRecentGalleries = async () => {
      try {
        const res = await axios.get(API_URL + "/api/galleries/public");

        if (!res.data.galleries || res.data.galleries.length === 0) {
          console.warn("⚠️ No galleries returned from API.");
        } else {
        }

        const recentGalleries = [...res.data.galleries].reverse();

        const galleriesWithImages = recentGalleries.map((gallery) => {
          const firstImage = gallery.cover_image
            ? `https://imagedelivery.net/Z1o5jP3Z0wrLOM-5XDvR8A/${gallery.cover_image}/${HOMEPAGE_CARD_VARIANT}`
            : null;
          return { ...gallery, firstImage };
        });

        setGalleries(galleriesWithImages);
      } catch (err) {
        console.error("❌ Error fetching galleries:", err.response?.data || err.message);
        setError("Failed to load recent galleries.");
      }
    };

    fetchRecentGalleries();
  }, []);

  useEffect(() => {
    const updateSlidesPerView = () => {
      if (window.innerWidth >= 1024) {
        setSlidesPerView(3);
      } else if (window.innerWidth >= 640) {
        setSlidesPerView(2);
      } else {
        setSlidesPerView(1);
      }
    };

    updateSlidesPerView();
    window.addEventListener("resize", updateSlidesPerView);
    return () => window.removeEventListener("resize", updateSlidesPerView);
  }, []);

  useEffect(() => {
    setCurrentIndex(0);
  }, [slidesPerView, visibleGalleries.length]);

  useEffect(() => {
    if (!loadMoreRef.current || visibleCount >= galleries.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + CARD_BATCH_SIZE, galleries.length));
        }
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [visibleCount, galleries.length]);

  useEffect(() => {
    if (visibleGalleries.length <= slidesPerView || isPaused) return undefined;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const nextPage = (Math.floor(prev / slidesPerView) + 1) % totalPages;
        return nextPage * slidesPerView;
      });
    }, 4500);

    return () => clearInterval(interval);
  }, [visibleGalleries.length, slidesPerView, totalPages, isPaused]);

  const handlePrev = () => {
    setCurrentIndex((prev) => {
      const prevPage = (Math.floor(prev / slidesPerView) - 1 + totalPages) % totalPages;
      return prevPage * slidesPerView;
    });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => {
      const nextPage = (Math.floor(prev / slidesPerView) + 1) % totalPages;
      return nextPage * slidesPerView;
    });
  };

  return (
    <div
      className="p-6 max-w-6xl mx-auto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      {error && <p className="text-red-500 text-center">{error}</p>}

      {galleries.length === 0 ? (
        <p className="text-center text-gray-500 mt-8">No galleries available yet.</p>
      ) : (
        <div className="space-y-4">
          <div className="relative overflow-hidden" aria-live="polite">
            <p className="sr-only">
              Showing page {currentPage + 1} of {Math.max(totalPages, 1)}
            </p>
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${(currentIndex / slidesPerView) * 100}%)` }}
            >
              {visibleGalleries.map((gallery) => (
                <div
                  key={gallery.id}
                  className="shrink-0 px-2"
                  style={{ width: `${100 / slidesPerView}%` }}
                >
                  <Link
                    to={gallery.slug ? `/galleries/${gallery.slug}` : `/gallery/${gallery.id}`}
                    className="relative group block overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-transform duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-500/60"
                  >
                    <div className="relative w-full aspect-[3/4]">
                      {gallery.firstImage ? (
                        <img
                          src={gallery.firstImage}
                          alt={gallery.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 transform scale-100 group-hover:scale-105 rounded-lg"
                          loading="lazy"
                        />
                      ) : (
                        <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center text-gray-600 rounded-lg">
                          No Cover Image
                        </div>
                      )}

                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>

                      <h3 className="absolute inset-0 flex items-center justify-center text-white text-lg font-semibold text-center px-2">
                        {gallery.title}
                      </h3>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={handlePrev}
              aria-label="Previous collections"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-500/60"
            >
              ←
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, pageIndex) => (
                <button
                  key={`page-dot-${pageIndex}`}
                  type="button"
                  onClick={() => setCurrentIndex(pageIndex * slidesPerView)}
                  aria-label={`Go to collections page ${pageIndex + 1}`}
                  className={`h-2.5 w-2.5 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 ${
                    currentPage === pageIndex ? "bg-gray-800" : "bg-gray-300 hover:bg-gray-500"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={handleNext}
              aria-label="Next collections"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-500/60"
            >
              →
            </button>
          </div>

          {visibleCount < galleries.length && (
            <div ref={loadMoreRef} className="h-4 w-full" aria-hidden="true" />
          )}
        </div>
      )}
    </div>
  );
};

export default SelectedWorkArt;
